<template>
    <v-navigation-drawer :expand-on-click="false" mini-variant-width="85" :width="pcOnly ? 300 : 335" :mini-variant="mini" overlay-color="#000" overlay-opacity="0.65" app :permanent="pcOnly" fixed v-model="show" :temporary="phoneOnly" class="app-menu lg-shadow" data-tour="side-menu">
        <div v-if="phoneOnly" class="drawer-header pt-5 pt-lg-7">
            <div class="logo-wrapper d-flex align-center justify-space-between px-5" :style="iosSafeArea ? `padding-top: 40px!important` : ''">
                <logo />

                <v-btn large class="mb-2 ms-2" icon text @click.prevent="show=false">
                    <v-icon size="24">mdi-menu</v-icon>
                </v-btn>
            </div>
        </div>

        <v-list-item v-else class="py-2" :class="{'px-4': !mini && pcOnly}">
            <v-list-item-content v-if="!mini">
                <logo :height="48" />
            </v-list-item-content>
            <v-list-item-content v-else class="d-flex justify-center">
                <img src="/img/icon-logo.svg" alt="Logo" style="width: 40px; height: 40px;" />
            </v-list-item-content>

            <v-btn v-if="!mini" large class="ml-2 mb-2" icon text @click.prevent="mini = !mini">
                <v-icon size="24">mdi-chevron-left</v-icon>
            </v-btn>
        </v-list-item>

        <v-list-item v-if="mini" class="mb-3">
            <v-btn color="primary" large class="mb-2" icon text @click.prevent="mini = !mini">
                <v-icon size="24">mdi-chevron-right</v-icon>
            </v-btn>
        </v-list-item>

        <!-- User Profile Section -->
        <div v-if="user" class="user-profile-section">
            <div v-if="!mini" class="user-profile-card mx-4 my-3">
                <div class="user-id-corner">ID: {{ user.id }}</div>
                <div class="d-flex align-center pa-3 pt-4">
                    <v-avatar size="44" color="primary" class="me-3 user-avatar">
                        <span class="white--text font-weight-medium" style="font-size: 16px;">
                            {{ userInitials }}
                        </span>
                    </v-avatar>
                    <div class="user-info flex-grow-1">
                        <div class="user-name">{{ user.name }}</div>
                        <div class="user-email">{{ user.email }}</div>
                    </div>
                </div>
            </div>
            <div v-else class="d-flex flex-column align-center mb-3">
                <v-avatar size="40" color="primary">
                    <span class="white--text font-weight-medium" style="font-size: 16px;">
                        {{ userInitials }}
                    </span>
                </v-avatar>
                <span class="user-id-mini mt-1">#{{ user.id }}</span>
            </div>
        </div>

        <v-card flat v-if="appData.ramadan_mode" class="my-4 my-lg-2 mx-4 pa-2 d-flex align-center justify-center" :class="{'purple lighten-5': !mini}">
            <div>
                <img src="/img/ramadan/ramadan.svg" :style="`width: ${mini ? 70 : 85}px; height: auto`" />
            </div>
        </v-card>

        <v-list  class="mt-3" :class="{'px-3': !mini && pcOnly}" nav dense>

            <!-- <v-divider class="mb-3" /> -->
            <v-list-item v-for="(item, i) in drawerRoutes" v-show="!item.pcOnly || pcOnly" color="primary" :key="i" link :to="item.to" :href="item.href" :target="item.href ? '_system' : ''" exact :class="{'v-list-item--active': $route.name === item.name, 'px-6': !mini}" :data-tour="item.name === 'courses' ? 'courses-menu' : undefined">
                <v-list-item-icon>
                    <v-badge small :value="!!item.chip && self[item.chip]" :content="self[item.chip]" overlap light :color="item.chipColor ? item.chipColor : 'ebadge'" left class="menu-item-badge">
                        <v-icon>{{ $route.name === item.name ? item.activeIcon || item.icon : item.icon }}</v-icon>
                    </v-badge>
                </v-list-item-icon>

                <v-list-item-content>
                    <v-list-item-title :style="$route.name === item.name ? 'font-weight: 500' : 'font-weight: 400!important'" style="font-size: 0.95rem; line-height: 1.4rem">{{item.text}}</v-list-item-title>
                </v-list-item-content>

            </v-list-item>

        </v-list>

        <!-- Monthly Performance Progress -->
        <div v-if="isStudent && !mini && homeData" class="performance-card mx-4 my-4">
            <div class="performance-title">Monthly Class Performance</div>
            <div class="performance-subtitle">Based on your class ratings</div>
            <div class="performance-percentage">{{ monthlyPerformance }}%</div>
            <div class="performance-segments">
                <div 
                    v-for="i in 20" 
                    :key="i" 
                    class="segment"
                    :class="{ 'filled': i <= filledSegments }"
                    :style="i <= filledSegments ? getSegmentStyle(i) : {}"
                ></div>
            </div>
        </div>

        <div v-if="phoneOnly" class="px-9 my-5">
            <v-btn block outlined class="mx-auto" @click="show = false; logoutDialog = true">
                <v-icon left>mdi-account-arrow-right-outline</v-icon>
                Logout
            </v-btn>
        </div>

        <!-- Policy / Agreement link (shown only when content exists for the user's role) -->
        <div v-if="!mini && (showStudentPolicy || showTeacherAgreement)" class="policy-link-wrapper">
            <a v-if="showStudentPolicy" href="#" class="policy-link" @click.prevent="studentClassesPolicyDialog = true">
                <v-icon size="18" class="me-2" color="indigo">mdi-file-document-outline</v-icon>
                <span>Student Classes Policy</span>
            </a>
            <a v-if="showTeacherAgreement" href="#" class="policy-link" @click.prevent="teacherAgreementDialog = true">
                <v-icon size="18" class="me-2" color="teal darken-1">mdi-handshake-outline</v-icon>
                <span>Teacher Agreement &amp; Guidelines</span>
            </a>
        </div>
        <div v-else-if="mini && (showStudentPolicy || showTeacherAgreement)" class="d-flex flex-column align-center my-2">
            <v-btn v-if="showStudentPolicy" icon small @click="studentClassesPolicyDialog = true" title="Student Classes Policy">
                <v-icon size="22" color="indigo">mdi-file-document-outline</v-icon>
            </v-btn>
            <v-btn v-if="showTeacherAgreement" icon small @click="teacherAgreementDialog = true" title="Teacher Agreement & Guidelines">
                <v-icon size="22" color="teal darken-1">mdi-handshake-outline</v-icon>
            </v-btn>
        </div>

        <v-divider class="my-5"></v-divider>
        
        <!-- Social Media & App Store Section -->
        <div class="social-app-section" :class="{'mini-mode': mini, 'mobile-bottom-spacing': phoneOnly}">
            <!-- Social Media Icons -->
            <div v-if="!mini" class="social-header">Follow Us</div>
            <div class="social-icons" :class="mini ? 'justify-center' : ''">
                <a href="https://www.facebook.com/profile.php?id=100089649577960" target="_system" class="social-link facebook" title="Facebook">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                </a>
                <a href="https://x.com/riwaqalquran" target="_system" class="social-link twitter" title="X (Twitter)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                </a>
                <a href="https://instagram.com/riwaqalquran" target="_system" class="social-link instagram" title="Instagram">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                    </svg>
                </a>
                <a href="https://www.youtube.com/@riwaqalquran" target="_system" class="social-link youtube" title="YouTube">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                </a>
                <a href="https://www.tiktok.com/@riwaqalquraninst" target="_system" class="social-link tiktok" title="TikTok">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                </a>
            </div>

            <!-- Get the App -->
            <div v-if="!capRunning" class="app-download-section">
                <div v-if="!mini" class="app-header">Get the App</div>
                <div class="store-links">
                    <a href="https://apps.apple.com/eg/app/riwaq-al-quran/id6739214342" target="_blank" class="store-link">
                        <img v-if="!mini" src="/img/appstore.svg" style="height: 40px;" />
                        <v-icon v-else size="32" color="#6B6B6B">mdi-apple</v-icon>
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.riwaqalquran.app" target="_blank" class="store-link">
                        <img v-if="!mini" src="/img/playstore.svg" style="height: 40px;" />
                        <img v-else src="/img/playstore-icon.svg" style="height: 32px; width: 32px;" />
                    </a>
                </div>
            </div>
        </div>

        <logout-dialog v-model="logoutDialog" />

        <v-dialog v-model="studentClassesPolicyDialog" max-width="900" overlay-color="#000" overlay-opacity="0.7" scrollable>
            <v-card class="policy-dialog-card">
                <v-card-title class="policy-dialog-title">
                    <v-icon color="indigo" class="me-2">mdi-school-outline</v-icon>
                    Student Classes Policy
                </v-card-title>
                <v-divider />
                <v-card-text class="policy-dialog-text">
                    <div class="policy-html" v-html="appData.student_classes_policy"></div>
                </v-card-text>
                <v-divider />
                <v-card-actions class="px-4 py-3">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" depressed @click="studentClassesPolicyDialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="teacherAgreementDialog" max-width="900" overlay-color="#000" overlay-opacity="0.7" scrollable>
            <v-card class="policy-dialog-card">
                <v-card-title class="policy-dialog-title">
                    <v-icon color="teal darken-1" class="me-2">mdi-handshake-outline</v-icon>
                    Teacher Agreement And Guidelines
                </v-card-title>
                <v-divider />
                <v-card-text class="policy-dialog-text">
                    <div class="policy-html" v-html="appData.teacher_agreement_guidelines"></div>
                </v-card-text>
                <v-divider />
                <v-card-actions class="px-4 py-3">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" depressed @click="teacherAgreementDialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-navigation-drawer>

</template>

<script>
import GlobalMethods from '@/helpers/global-methods'
import GlobalComputed from '@/helpers/global-computed'
import Logo from './logo'
import drawerRoutes from '@/helpers/links'
import LogoutDialog from '../default/dialogs/logout'

export default {
    name: 'app-menu',
    props: ['value'],
    data(){
        return {
            self: this,

            show: false,
            logoutDialog: false,
            studentClassesPolicyDialog: false,
            teacherAgreementDialog: false,

            mini: false,
        }
    },
    components: {
        Logo,
        LogoutDialog,
        // StoreBadge
    },
    watch :{
        show(show){
            if (!show){
                this.$emit('input', false); // close
            }
        },
        value(show){
            this.show = show;
        }
    },
    computed: {
        ...GlobalComputed,
        walletDisabled(){
            return !this.appData.enable_app_w;
        },
        hideDashboard(){
            return this.isStudent && (!this.user.my_courses || this.user.my_courses.length === 0);
        },
        showStudentPolicy(){
            if (!this.isStudent) return false;
            const policy = this.appData && this.appData.student_classes_policy;
            return !!(policy && String(policy).trim());
        },
        showTeacherAgreement(){
            if (!this.isTeacher) return false;
            const agreement = this.appData && this.appData.teacher_agreement_guidelines;
            return !!(agreement && String(agreement).trim());
        },
        drawerRoutes(){
            let routes = this.isAuthed ? (this.isTeacher ? drawerRoutes.auth.teacher : drawerRoutes.auth.student) : drawerRoutes.guest;
            const self = this;
            return routes.filter(route=>{
                if (route.hide){
                    return !self[route.hide];
                }
                return true;
            })
        },
        homeData(){
            return this.$store.state.userModule.homeData;
        },
        monthlyPerformance(){
            return this.homeData ? (this.homeData.monthly_performance || 0) : 0;
        },
        filledSegments(){
            // 20 segments total, calculate how many should be filled based on percentage
            return Math.round((this.monthlyPerformance / 100) * 20);
        },
        userInitials(){
            if (!this.user || !this.user.name) return '?';
            const nameParts = this.user.name.trim().split(' ');
            if (nameParts.length >= 2) {
                // First name + Last name initials
                return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
            }
            // Just first name initial
            return nameParts[0][0].toUpperCase();
        },
    },
    methods: {
        openRoute(route){
            this.show = false;
            this.$router.push(route);
        },
        openProfile(){
            let isProfile = this.$route.name === 'profile' && this.$route.params.id == this.user.id;
            if (!isProfile){
                this.$router.push({name: 'profile', params: {id: this.user.id}});
            }
        },
        getSegmentStyle(index){
            // Create gradient effect from darker orange to lighter orange
            const totalFilled = this.filledSegments;
            const ratio = index / totalFilled;
            // Gradient from #D4780A (darker) to #E89A3C (lighter)
            const r = Math.round(212 + (232 - 212) * ratio);
            const g = Math.round(120 + (154 - 120) * ratio);
            const b = Math.round(10 + (60 - 10) * ratio);
            return {
                backgroundColor: `rgb(${r}, ${g}, ${b})`
            };
        },
        ...GlobalMethods
    }
}
</script>

<style lang="scss" scoped>
    .app-menu{
        will-change: auto!important;
        border: none!important;
        
        .v-list-item{
            border-left: 3px solid transparent;
            &.v-list-item--active{
                border-left: 3px solid;
                border-top-left-radius: 0!important;
                border-bottom-left-radius: 0!important;
                // box-shadow: 0 1px 5px rgba(#000, 0.1);
            }

        }
        .theme--light.v-list-item--active:before, .theme--light.v-list-item--active:hover:before, .theme--light.v-list-item:focus:before{
            opacity: 0.08!important;
        }
    }

    /* User Profile Section */
    .user-profile-card {
        background: #FFFFFF;
        border-radius: 12px;
        border: 1px solid #E8E8E8;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;

        &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
    }

    .user-id-corner {
        position: absolute;
        top: 0;
        right: 0;
        background: linear-gradient(135deg, #FFF8EE 0%, #FFF3E0 100%);
        padding: 4px 10px;
        border-radius: 0 12px 0 8px;
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 10px;
        color: #E79007;
        letter-spacing: 0.3px;
        border-left: 1px solid rgba(231, 144, 7, 0.15);
        border-bottom: 1px solid rgba(231, 144, 7, 0.15);
        z-index: 1;
    }

    .user-info {
        overflow: hidden;
        padding-right: 50px; // Space for the ID badge
    }

    .user-name {
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 14px;
        color: #3D3D3D;
        line-height: 1.4;
        margin-bottom: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .user-email {
        font-family: 'Inter', sans-serif;
        font-weight: 400;
        font-size: 12px;
        color: #8E8E8E;
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .user-id-mini {
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 10px;
        color: #E79007;
    }

    .performance-card {
        background: #FFFFFF;
        border-radius: 12px;
        padding: 16px 12px;
        border: 1px solid #E8E8E8;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .performance-title {
        font-family: 'Inter', sans-serif;
        font-weight: 700;
        font-size: 16px;
        color: #3D3D3D;
        margin-bottom: 2px;
    }

    .performance-subtitle {
        font-family: 'Inter', sans-serif;
        font-weight: 400;
        font-size: 14px;
        color: #8E8E8E;
        margin-bottom: 8px;
    }

    .performance-percentage {
        font-family: 'Inter', sans-serif;
        font-weight: 500;
        font-size: 14px;
        color: #5C5C5C;
        text-align: right;
        margin-bottom: 6px;
    }

    .performance-segments {
        display: flex;
        gap: 3px;
        width: 100%;
    }

    .segment {
        flex: 1;
        height: 24px;
        background: #D1D1D1;
        border-radius: 3px;
        transition: background-color 0.3s ease;

        &.filled {
            background: #E79007;
        }
    }

    /* Social Media & App Section */
    .social-app-section {
        padding: 0 36px 32px 36px;
        
        &.mobile-bottom-spacing {
            padding-bottom: 80px; // Extra bottom padding for mobile to ensure content is scrollable
        }
        
        &.mini-mode {
            padding: 0 0 32px 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            
            .social-icons {
                flex-direction: column;
                align-items: center;
                gap: 8px;
            }
            
            .social-link {
                width: 36px;
                height: 36px;
            }
            
            .app-download-section {
                margin-top: 16px;
                display: flex;
                justify-content: center;
                width: 100%;
                
                .store-links {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                }
                
                .store-link {
                    padding: 0;
                    background: transparent;
                    border: none;
                    display: flex;
                    justify-content: center;
                    
                    &:hover {
                        background: transparent;
                        transform: scale(1.1);
                    }
                }
            }
        }
    }
    
    .social-header,
    .app-header {
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 12px;
        color: #8E8E8E;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        margin-bottom: 12px;
    }
    
    .social-icons {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    
    .social-link {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border-radius: 10px;
        background: #F5F5F5;
        color: #6B6B6B;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        text-decoration: none;
        
        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        }
        
        &.facebook:hover {
            background: #1877F2;
            color: #FFFFFF;
        }
        
        &.twitter:hover {
            background: #1DA1F2;
            color: #FFFFFF;
        }
        
        &.instagram:hover {
            background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
            color: #FFFFFF;
        }
        
        &.youtube:hover {
            background: #FF0000;
            color: #FFFFFF;
        }
        
        &.tiktok:hover {
            background: #000000;
            color: #FFFFFF;
        }
        
        svg {
            transition: transform 0.2s ease;
        }
        
        &:hover svg {
            transform: scale(1.1);
        }
    }
    
    .app-download-section {
        margin-top: 20px;
        display: flex;
        flex-direction: column;
    }
    
    .store-links {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
    
    .policy-link-wrapper {
        padding: 4px 24px 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .policy-link {
        display: flex;
        align-items: center;
        padding: 8px 4px;
        font-family: 'Inter', sans-serif;
        font-size: 13px;
        font-weight: 500;
        color: #6B6B6B;
        text-decoration: none;
        border-radius: 6px;
        transition: background-color 0.2s ease, color 0.2s ease;

        &:hover {
            background: #F5F5F5;
            color: #3D3D3D;
        }
    }

    .store-link {
        display: inline-flex;
        align-items: center;
        padding: 8px 16px;
        background: #F5F5F5;
        border-radius: 10px;
        border: 1px solid #E8E8E8;
        transition: all 0.25s ease;
        text-decoration: none;
        width: fit-content;
        
        &:hover {
            background: #FFFFFF;
            border-color: #D0D0D0;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        img, .v-icon {
            transition: transform 0.2s ease;
        }
        
        &:hover img, &:hover .v-icon {
            transform: scale(1.02);
        }
    }
</style>
<style lang="scss">
    .v-navigation-drawer__border{
        display: none;
    }

    /* Policy / Agreement dialog */
    .policy-dialog-card {
        border-radius: 12px !important;
    }
    .policy-dialog-title {
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 18px;
        color: #3D3D3D;
        padding: 18px 20px;
    }
    .policy-dialog-text {
        padding: 20px 24px !important;
        max-height: 70vh;
    }

    /* Render output produced by Quill (vue2-editor) */
    .policy-html {
        font-family: 'Inter', sans-serif;
        font-size: 14.5px;
        line-height: 1.75;
        color: #3D3D3D;
        word-break: break-word;

        h1, h2, h3, h4 {
            font-weight: 600;
            color: #2A2A2A;
            margin: 1.2em 0 0.5em;
            line-height: 1.3;
        }
        h1 { font-size: 1.8em; }
        h2 { font-size: 1.5em; }
        h3 { font-size: 1.25em; }
        h4 { font-size: 1.1em; }

        p {
            margin: 0 0 0.85em;
        }
        p:last-child { margin-bottom: 0; }

        ul, ol {
            padding-left: 1.5em;
            margin: 0 0 0.85em;
        }
        li { margin: 0.2em 0; }

        strong, b { font-weight: 600; }
        em, i { font-style: italic; }
        u { text-decoration: underline; }
        s { text-decoration: line-through; }

        a {
            color: #1976D2;
            text-decoration: underline;
        }

        blockquote {
            border-left: 3px solid #E0E0E0;
            padding: 4px 14px;
            margin: 0.5em 0;
            color: #555;
            background: #FAFAFA;
            border-radius: 0 6px 6px 0;
        }

        pre, code {
            font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
            font-size: 0.92em;
        }
        pre {
            background: #F5F5F5;
            padding: 10px 14px;
            border-radius: 6px;
            overflow-x: auto;
            white-space: pre-wrap;
        }
        code {
            background: #F0F0F0;
            padding: 1px 5px;
            border-radius: 3px;
        }

        img { max-width: 100%; height: auto; border-radius: 6px; }

        /* Quill alignment classes */
        .ql-align-center { text-align: center; }
        .ql-align-right  { text-align: right; }
        .ql-align-justify { text-align: justify; }

        /* Quill indent classes */
        .ql-indent-1 { padding-left: 3em; }
        .ql-indent-2 { padding-left: 6em; }
        .ql-indent-3 { padding-left: 9em; }
        .ql-indent-4 { padding-left: 12em; }
    }
</style>
